import * as BABYLON from '@babylonjs/core';
import * as GUI from '@babylonjs/gui';
import { EventsManager } from '@game-libs/events-manager';
import { DungeonEvents } from '../../types/game-events';
import { computeDungeonGoldReward, GOLD_ENEMY_MULTIPLIER, ENEMY_LEVEL_MAX } from './rewards/GoldRewardConfig';

const ASSETS_BASE_PATH = 'dungeon-crawler';

export type EnemyId = 'spider' | 'blob' | string;

export interface DungeonEnemyOptions {
  /** Enemy type id; sprite is loaded from `${basePath}/${id}.png` (e.g. spider → spider.png, blob → blob.png) */
  id: EnemyId;
  /** Health amount; enemy dies when it reaches 0 (e.g. blob 1, spider 3) */
  health: number;
  /** Override sprite URL; if set, `id` is not used for the image path */
  spriteUrl?: string;
  /** Offset from the ground plane (default 0) */
  yOffset?: number;
  /** Spritesheet columns (default 2) */
  spriteCols?: number;
  /** Spritesheet rows (default 1) */
  spriteRows?: number;
  size?: number;
  renderingGroupId?: number;
  /** Advanced texture for GUI (required for health display) */
  advancedTexture?: GUI.AdvancedDynamicTexture;
  /** Camera reference for visibility checks */
  camera?: BABYLON.Camera;
  /** Tile size for battle trigger distance */
  tileSize?: number;
  /** Show debug visualization (collision box, rays) */
  showDebug?: boolean;
  /** Animation speed multiplier (default 1). Higher = faster sprite frames. Frame duration = FRAME_DURATION_MS / animationSpeed */
  animationSpeed?: number;
  /**
   * Dungeon level; gold reward is based on player gold at entry × level × enemy multiplier.
   * Default 1 if omitted.
   */
  dungeonLevel?: number;
  /**
   * Player's gold when they entered the dungeon; used to compute defeat gold (1%–5% × level × enemy multiplier).
   */
  playerGoldAtEntry?: number;
  /**
   * Enemy tier (1–10) from spawn config; scales gold reward (tier 1 = 10%, tier 10 = 100%).
   */
  level?: number;
  /** Called when this enemy is defeated with the gold amount awarded. */
  onDefeated?: (gold: number) => void;
}

export class DungeonEnemy {
  readonly mesh: BABYLON.Mesh;
  readonly scene: BABYLON.Scene;
  readonly id: EnemyId;
  private _health: number;
  private maxHealth: number;
  private readonly yOffset: number;
  private material: BABYLON.StandardMaterial;
  private texture: BABYLON.Texture;
  private readonly spriteCols: number;
  private readonly spriteRows: number;
  private pointerObserver: BABYLON.Nullable<BABYLON.Observer<BABYLON.PointerInfo>> = null;
  private _disposed = false;
  private frameTimeAccumulator = 0;
  private currentFrame = 0;
  private static readonly FRAME_DURATION_MS = 500;
  private readonly animationSpeed: number;
  private camera: BABYLON.Camera | null = null;
  private inBattle: boolean = false;
  private hasBeenDefeated: boolean = false;
  private tileSize: number = 4;
  // private isPlayerRotating: boolean = false;
  private showDebug: boolean = false;
  private debugBox: BABYLON.Mesh | null = null;
  private collisionSphere: BABYLON.Mesh | null = null; // Actually a box, kept name for compatibility

  private readonly dungeonLevel: number;
  private readonly playerGoldAtEntry: number;
  private readonly enemyLevel: number;
  private readonly onDefeated?: (gold: number) => void;

  // GUI elements
  private advancedTexture: GUI.AdvancedDynamicTexture | null = null;
  private heartsContainer: GUI.StackPanel | null = null;
  private heartImages: GUI.Image[] = [];

  // Flicker effect
  private flickerTimer: number | null = null;
  private flickerTimeout: number | null = null;

  // Reusable vectors to avoid per-frame allocations in hasPlayerPassedAndFacing
  private readonly _tempVec1 = new BABYLON.Vector3();
  private readonly _tempVec2 = new BABYLON.Vector3();

  private eventsManager = EventsManager();

  constructor(
    scene: BABYLON.Scene,
    position: BABYLON.Vector3,
    options: DungeonEnemyOptions
  ) {
    this.scene = scene;
    const {
      id,
      health,
      spriteUrl,
      yOffset = 0,
      spriteCols = 2,
      spriteRows = 1,
      size = 3,
      renderingGroupId = 0,
      advancedTexture,
      camera,
    } = options;

    this.id = id;
    this._health = health;
    this.maxHealth = health;
    this.yOffset = yOffset;
    this.spriteCols = spriteCols;
    this.spriteRows = spriteRows;
    this.advancedTexture = advancedTexture || null;
    this.camera = camera || null;
    this.tileSize = options.tileSize || 4;
    this.showDebug = options.showDebug || false;
    this.animationSpeed = options.animationSpeed ?? 1;
    this.dungeonLevel = options.dungeonLevel ?? 1;
    this.playerGoldAtEntry = options.playerGoldAtEntry ?? 0;
    this.enemyLevel = options.level ?? ENEMY_LEVEL_MAX;
    this.onDefeated = options.onDefeated;

    const resolvedSpriteUrl =
      spriteUrl ?? `${ASSETS_BASE_PATH}/${id}.png`;

    this.mesh = BABYLON.MeshBuilder.CreateGround(
      `enemy_${id}_${Date.now()}`,
      { width: size, height: size },
      scene
    );
    this.mesh.rotation.x = -Math.PI / 2;
    this.mesh.position.copyFrom(position);
    this.mesh.isPickable = true;
    this.mesh.renderingGroupId = renderingGroupId;

    this.texture = new BABYLON.Texture(
      resolvedSpriteUrl,
      scene,
      false,
      true,
      BABYLON.Texture.NEAREST_SAMPLINGMODE
    );
    this.texture.hasAlpha = true;
    this.texture.uScale = 1 / spriteCols;
    this.texture.vScale = 1 / spriteRows;
    this.texture.uOffset = 0;
    this.texture.vOffset = 0;

    this.material = new BABYLON.StandardMaterial(`enemy_mat_${this.mesh.name}`, scene);
    this.material.diffuseTexture = this.texture;
    this.material.useAlphaFromDiffuseTexture = true;
    this.material.transparencyMode = BABYLON.Material.MATERIAL_ALPHATEST;
    this.material.alphaCutOff = 0.5;
    this.material.backFaceCulling = false;
    this.material.emissiveColor = new BABYLON.Color3(0.7, 0.7, 0.7);
    this.material.specularColor = BABYLON.Color3.Black();
    this.material.alpha = 1; // Ensure alpha is 1 (will be controlled by mesh visibility instead)

    this.mesh.material = this.material;

    // Start invisible
    this.mesh.isVisible = false;

    // Create an invisible collision box for reliable raycasting
    // The flat plane mesh can be missed by rays depending on angle
    // Use a box instead of sphere for better performance
    this.collisionSphere = BABYLON.MeshBuilder.CreateBox(
      `enemy_collision_${this.id}_${Date.now()}`,
      { width: size, height: size, depth: size },
      scene
    );
    this.collisionSphere.position = position.clone();
    this.collisionSphere.isVisible = false; // Always invisible
    this.collisionSphere.isPickable = true; // So facing ray can hit enemy for cursor (click still uses mesh + inBattle)

    // Create debug visualization box if enabled
    if (this.showDebug) {
      this.debugBox = BABYLON.MeshBuilder.CreateBox(
        `enemy_debug_${this.id}_${Date.now()}`,
        { width: size, height: 4, depth: size },
        scene
      );
      this.debugBox.position = position.clone();
      this.debugBox.position.y = 2; // Center at height 2

      const debugMat = new BABYLON.StandardMaterial(`enemy_debug_mat_${this.id}`, scene);
      debugMat.diffuseColor = new BABYLON.Color3(1, 1, 0); // Yellow
      debugMat.alpha = 0.3;
      debugMat.wireframe = false;
      this.debugBox.material = debugMat;
      this.debugBox.isPickable = false; // Don't block clicks

      // Also show the collision box in debug mode
      this.collisionSphere.isVisible = true;
      const boxDebugMat = new BABYLON.StandardMaterial(`box_debug_mat_${this.id}`, scene);
      boxDebugMat.diffuseColor = new BABYLON.Color3(0, 1, 1); // Cyan
      boxDebugMat.alpha = 0.2;
      boxDebugMat.wireframe = true;
      this.collisionSphere.material = boxDebugMat;
      // Debug mode: collision box and mesh info available here for visualization.
    }
    // Enemy instantiated at position with id/size/visibility; consider emitting e.g. DUNGEON_ENEMY_SPAWNED here.

    this.pointerObserver = scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type !== BABYLON.PointerEventTypes.POINTERDOWN) return;

      // Only allow clicks during battle
      if (!this.inBattle) {
        // Click on enemy while not in battle; consider emitting e.g. DUNGEON_ENEMY_CLICK_NOT_IN_BATTLE.
        return;
      }
      if (!this.isAlive()) {
        // Click on defeated enemy; consider emitting if needed.
        return;
      }
      if (this.flickerTimer !== null) {
        // Ignore clicks while flickering (damage feedback)
        return;
      }

      this.eventsManager.emit(DungeonEvents.ATTACK_ENEMY);
      const evt = pointerInfo.event;
      const canvas = scene.getEngine().getRenderingCanvas();
      if (!canvas || !evt) return;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (evt.clientX - rect.left) * scaleX;
      const y = (evt.clientY - rect.top) * scaleY;
      const pickInfo = scene.pick(x, y);

      if (!pickInfo.hit || pickInfo.pickedMesh !== this.mesh) {
        // Click missed this enemy mesh; consider emitting if needed.
        return;
      }
      // Player hit this enemy during battle; consider emitting e.g. DUNGEON_ENEMY_HIT before takeDamage.
      this.takeDamage(1);
    });
  }

  private static readonly HEART_SIZE_PX = 64;

  private createHealthGUI(): void {
    if (!this.advancedTexture) {
      // No advancedTexture: health hearts cannot be shown; consider emitting or handling in caller.
      return;
    }

    const size = DungeonEnemy.HEART_SIZE_PX;
    const padding = 4;
    const totalWidth = this.maxHealth * (size + padding) + padding * 2;

    this.heartsContainer = new GUI.StackPanel();
    this.heartsContainer.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
    this.heartsContainer.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_TOP;
    this.heartsContainer.isVertical = false;
    this.heartsContainer.top = '20px';
    this.heartsContainer.left = '20px';
    this.heartsContainer.width = `${totalWidth}px`;
    this.heartsContainer.height = `${size + padding * 2}px`;
    this.heartsContainer.paddingLeft = `${padding}px`;
    this.heartsContainer.paddingRight = `${padding}px`;
    this.advancedTexture.addControl(this.heartsContainer);

    const fullHeartUrl = `${ASSETS_BASE_PATH}/full_heart.png`;

    // One heart per max health; refreshHeartDisplay() toggles full vs empty when damage is taken
    for (let i = 0; i < this.maxHealth; i++) {
      const heartImage = new GUI.Image(`heart_${this.mesh.name}_${i}`, fullHeartUrl);
      heartImage.width = `${size}px`;
      heartImage.height = `${size}px`;
      heartImage.paddingLeft = `${padding / 2}px`;
      heartImage.paddingRight = `${padding / 2}px`;
      heartImage.stretch = GUI.Image.STRETCH_UNIFORM;
      this.heartImages.push(heartImage);
      this.heartsContainer.addControl(heartImage);
    }
    this.refreshHeartDisplay();
  }

  /** Update heart images: full for current health, empty for lost. */
  private refreshHeartDisplay(): void {
    const fullUrl = `${ASSETS_BASE_PATH}/full_heart.png`;
    const emptyUrl = `${ASSETS_BASE_PATH}/empty_heart.png`;
    for (let i = 0; i < this.heartImages.length; i++) {
      this.heartImages[i].source = i < this._health ? fullUrl : emptyUrl;
    }
  }

  private flicker(): void {
    // Clear any existing flicker interval to restart the effect
    if (this.flickerTimer !== null) {
      window.clearInterval(this.flickerTimer);
      this.flickerTimer = null;
    }

    const originalAlpha = this.material.alpha || 1;
    const flickerDuration = 500;
    const flickerInterval = 50;
    let elapsed = 0;

    this.flickerTimer = window.setInterval(() => {
      elapsed += flickerInterval;

      // Toggle visibility
      this.material.alpha = this.material.alpha === 0 ? originalAlpha : 0;

      if (elapsed >= flickerDuration) {
        this.clearFlickerInterval();
        this.material.alpha = originalAlpha;
      }
    }, flickerInterval);
  }

  private clearFlickerInterval(): void {
    if (this.flickerTimer !== null) {
      window.clearInterval(this.flickerTimer);
      this.flickerTimer = null;
    }
  }

  get health(): number {
    return this._health;
  }

  isAlive(): boolean {
    return this._health > 0;
  }

  isInBattle(): boolean {
    return this.inBattle;
  }

  /** True while damage flicker is active; attacks should be ignored during this time. */
  isFlickering(): boolean {
    return this.flickerTimer !== null;
  }

  getCollisionMesh(): BABYLON.Mesh | null {
    return this.collisionSphere;
  }

  /** Show a specific frame (0-based). Spritesheet is 1 row × 2 columns by default. */
  setFrame(frameIndex: number): void {
    const col = frameIndex % this.spriteCols;
    const row = Math.floor(frameIndex / this.spriteCols) % this.spriteRows;
    this.texture.uOffset = col / this.spriteCols;
    this.texture.vOffset = row / this.spriteRows;
  }

  takeDamage(amount: number): void {
    if (!this.isAlive()) return;

    // Always process damage
    this._health = Math.max(0, this._health - amount);
    this.refreshHeartDisplay();

    // Start flicker (if already flickering, restart it)
    this.flicker();

    // If health reached zero, schedule death after flicker completes
    if (!this.isAlive()) {
      // Clear any existing death timeout
      if (this.flickerTimeout !== null) {
        window.clearTimeout(this.flickerTimeout);
      }

      // Schedule death after flicker duration
      this.flickerTimeout = window.setTimeout(() => {
        this.die();
      }, 500);
    }
  }

  private die(): void {
    const gold = computeDungeonGoldReward(
      this.playerGoldAtEntry,
      this.dungeonLevel,
      GOLD_ENEMY_MULTIPLIER,
      this.enemyLevel
    );
    this.onDefeated?.(gold);
    this.hasBeenDefeated = true;
    this.inBattle = false;
    this.dispose();
  }

  /**
   * Start battle with this enemy
   */
  private startBattle(): void {
    if (this.inBattle || this.hasBeenDefeated) return;

    this.inBattle = true;
    this.eventsManager.emit(DungeonEvents.START_BATTLE);

    // Make enemy visible
    this.mesh.isVisible = true;
    this.material.alpha = 1; // Ensure material is fully visible

    // Create health GUI when battle starts
    if (this.advancedTexture && !this.heartsContainer) {
      this.createHealthGUI();
    }
  }

  /**
   * Check if player is facing this enemy
   * Only checks the next tile (not all tiles ahead)
   * Only works when player is still or moving (not rotating)
   */
  private isPlayerFacing(tileSize: number = 4): boolean {
    if (!this.camera || !this.collisionSphere) return false;

    const rayLength = tileSize * 1.5;
    const ray = this.camera.getForwardRay(rayLength); // Check only next tile
    if (!ray) return false;

    // Debug: Visualize the ray
    if (this.showDebug && ray) {
      const rayHelper = new BABYLON.RayHelper(ray);
      rayHelper.show(this.scene, new BABYLON.Color3(0, 1, 0)); // Green ray

      // Dispose the ray helper after a short time
      setTimeout(() => rayHelper.dispose(), 100);
    }

    // Use collision box for raycast (more reliable than flat plane, better performance than sphere)
    const hit = this.scene.pickWithRay(ray, (mesh) => mesh === this.collisionSphere);

    if (!hit?.hit) return false;

    // CRITICAL: Check minimum distance to prevent battle trigger when player backs into enemy
    // Player must be at least half a tile away to trigger battle
    const minDistance = tileSize * 0.75;
    const distance = hit.distance || 0;
    // Optional: when showDebug, emit or log ray hit / too-close for battle trigger debugging.

    // Only trigger if distance is between min and max
    return distance >= minDistance && distance <= rayLength;
  }

  /**
   * Check if player has passed the enemy (is on the opposite side and facing it)
   * Only checks the next tile behind
   */
  private hasPlayerPassedAndFacing(tileSize: number = 4): boolean {
    if (!this.camera) return false;

    // Reuse temp vectors to avoid allocations
    this.camera.position.subtractToRef(this.mesh.position, this._tempVec1);
    const len1 = this._tempVec1.length();
    if (len1 < 1e-6) return false;
    this._tempVec1.scaleInPlace(1 / len1);

    const ray = this.camera.getForwardRay(1);
    if (!ray?.direction) return false;
    this._tempVec2.copyFrom(ray.direction);
    const len2 = this._tempVec2.length();
    if (len2 < 1e-6) return false;
    this._tempVec2.scaleInPlace(1 / len2);

    const dotProduct = BABYLON.Vector3.Dot(this._tempVec1, this._tempVec2);
    // Negative dot product means facing opposite directions (player passed and looking back)
    return dotProduct < -0.5 && this.isPlayerFacing(tileSize);
  }

  /** Rotate to face the player (camera) in the XZ plane and advance sprite animation. */
  update(camera: BABYLON.Camera, isRotating: boolean = false): void {
    this.camera = camera;
    // this.isPlayerRotating = isRotating;

    // Skip if already defeated
    if (this.hasBeenDefeated) return;

    // Check if battle should start (only when player is not rotating)
    if (!this.inBattle) {
      // Distance cull: skip raycasts if player is too far to trigger battle
      const dx = camera.position.x - this.mesh.position.x;
      const dz = camera.position.z - this.mesh.position.z;
      const distSq = dx * dx + dz * dz;
      const maxDist = this.tileSize * 1.2;
      if (distSq > maxDist * maxDist) {
        return;
      }

      const isFacing = this.isPlayerFacing(this.tileSize);
      const hasPassed = this.hasPlayerPassedAndFacing(this.tileSize);

      if (isFacing || hasPassed) {
        // Battle trigger condition met (facing or passed and facing); startBattle() runs next.
        this.startBattle();
      }
      return; // Don't update if not in battle
    }

    // Battle is active - update normally
    if (!this.isAlive()) return;

    const dx = camera.position.x - this.mesh.position.x;
    const dz = camera.position.z - this.mesh.position.z;
    this.mesh.rotation.y = Math.atan2(dx, dz);
    this.mesh.position.y = 1 + this.yOffset;

    // Keep collision box at same position as mesh
    if (this.collisionSphere) {
      this.collisionSphere.position.copyFrom(this.mesh.position);
    }

    const frameDurationMs = DungeonEnemy.FRAME_DURATION_MS / this.animationSpeed;
    const deltaMs = this.scene.getEngine().getDeltaTime();
    this.frameTimeAccumulator += deltaMs;
    while (this.frameTimeAccumulator >= frameDurationMs) {
      this.frameTimeAccumulator -= frameDurationMs;
      this.currentFrame = (this.currentFrame + 1) % (this.spriteCols * this.spriteRows);
      this.setFrame(this.currentFrame);
    }
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;

    // Clear flicker interval
    this.clearFlickerInterval();

    // Clear death timeout
    if (this.flickerTimeout !== null) {
      window.clearTimeout(this.flickerTimeout);
      this.flickerTimeout = null;
    }

    // Remove pointer observer
    if (this.pointerObserver) {
      this.scene.onPointerObservable.remove(this.pointerObserver);
      this.pointerObserver = null;
    }

    // Dispose GUI elements
    if (this.heartsContainer && this.advancedTexture) {
      this.advancedTexture.removeControl(this.heartsContainer);
      this.heartsContainer.dispose();
      this.heartsContainer = null;
    }

    if (this.heartImages.length > 0) {
      this.heartImages.forEach(heart => heart.dispose());
      this.heartImages = [];
    }

    // Dispose mesh, material, and texture
    this.mesh.dispose();
    this.material.dispose();
    this.texture.dispose();

    // Dispose collision box
    if (this.collisionSphere) {
      this.collisionSphere.dispose();
      this.collisionSphere = null;
    }

    // Dispose debug box if it exists
    if (this.debugBox) {
      this.debugBox.dispose();
      this.debugBox = null;
    }
  }
}